import { Add, Archive, CloudDownload, Edit, Print } from '@mui/icons-material';
import {
  Box,
  ButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import { Button, Loader, Page, PageTitleWithButton } from '../../../../components';
import { UserContext, VendorPageContext } from '../../../../context';
import {
  addTemplateToPunchlist,
  getPunchlistDetails,
  updatePunchlist,
  getPunchlistTodoListPdf
} from '../../../../fetch';
import { downloadFile, sortPunchlistTodoItems } from '../../../../helpers';
import { IPunchlistDetail, IPunchlistTodoItem } from '../../../../models';
import { TodoDetailModal } from '../punchlist-item';
import { PunchlistDetailsFilters } from './Punchlist-details-filters';
import { PunchlistDetailsTodoList } from './PunchlistDetailsTodoList';
import { PunchlistModal } from './punchlist-modal';
import { PunchlistTemplateModal } from './template-modal';

interface IPunchlistDetails {
  route?: string;
}

export const PunchlistDetails: FC<IPunchlistDetails> = ({ route }) => {
  const { punchlistId: paramsPunchlistId }: { punchlistId: string } = useParams();
  const { isAdmin, isField, isFieldAdmin, isVendor } = useContext(UserContext);
  const { vendorSageId } = useContext(VendorPageContext);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isUpdating, setUpdating] = useState<boolean>(false);
  const [loadingTodos, setLoadingTodos] = useState(true);
  const [punchlistDetail, setPunchlistDetails] = useState<IPunchlistDetail | null>(null);
  const [isTodoModalOpen, showTodoModal] = useState<boolean>(false);
  const [isTemplateModalOpen, showIsTemplateModalOpen] = useState<boolean>(false);
  const [isPunchlistModalOpen, showPunchlistModal] = useState<boolean>(false);
  const [currentTodoId, setCurrentTodoId] = useState<number | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const isMdMobile = useMediaQuery('(max-width: 1200px)');
  const history = useHistory();
  // Add support to link user directly to opening todo item modal
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const punchlistItemId = params.get('punchlistItemId');
  const printRef = useRef();

  const vendors = useMemo(() => {
    return punchlistDetail?.punchListItems?.reduce((prev, curr) => {
      if (
        curr?.vendorName &&
        curr?.vendorSageId &&
        curr?.itemStatuses !== 'Complete' &&
        !prev.some(vendor => vendor?.id === curr?.vendorSageId)
      ) {
        prev.push({ name: curr?.vendorName, id: curr?.vendorSageId });
      }

      return prev;
    }, []);
  }, [punchlistDetail]);

  const getEmployeeFilterName = (employee: IPunchlistTodoItem) => {
    if (employee.completedByDisplayName) {
      return employee.completedByDisplayName;
    }
    if (employee.assignedToDisplayName) {
      return employee.assignedToDisplayName;
    }

    return employee.assignedTo ?? `Employee Sage Id: ${employee.vendorSageId}`;
  };

  const employees = useMemo(() => {
    return punchlistDetail?.punchListItems
      ?.filter(punchListItem => {
        const isInternalUser = !punchListItem?.assignedTo?.includes('@');
        return isInternalUser;
      })
      ?.reduce((prev, curr) => {
        // if the employee is already in the list, don't add it again
        if (
          curr?.assignedTo &&
          prev.some(employee => employee?.name === curr?.assignedToDisplayName)
        ) {
          return prev;
        } else if (curr?.assignedTo) {
          prev.push({
            name: getEmployeeFilterName(curr),
            id: getEmployeeFilterName(curr)
          });
        }
        return prev;
      }, []);
  }, [punchlistDetail]);

  const [filteredPunchlistDetails, setFilteredPunchlistDetails] = useState(punchlistDetail);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('none');
  const filterByVendor = (vendorId: string | null, punchlistItems: IPunchlistTodoItem[]) => {
    if (vendorId === 'none') {
      return punchlistItems;
    }
    const filteredItems = punchlistItems?.filter(item => {
      return item.vendorSageId === +vendorId;
    });

    return filteredItems;
  };

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('none');
  const filterByEmployee = (
    selectedEmployeeId: string | null,
    punchlistItems: IPunchlistTodoItem[]
  ) => {
    if (selectedEmployeeId === 'none') {
      return punchlistItems;
    }
    const filteredItems = punchlistItems?.filter(item => {
      return getEmployeeFilterName(item) === selectedEmployeeId;
    });

    return filteredItems;
  };

  const filterPunchlist = (vendorId: string | null, selectedEmployeeId: string | null) => {
    const filteredByVendor = filterByVendor(vendorId, punchlistDetail.punchListItems);
    const filteredItems = filterByEmployee(selectedEmployeeId, filteredByVendor);

    setFilteredPunchlistDetails({
      ...punchlistDetail,
      punchListItems: filteredItems
    });
  };

  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const fetchPunchlistDetails = async (showLoader?: boolean, resetFilters?: boolean) => {
    try {
      if (showLoader) {
        setLoadingTodos(true);
      }
      const res = await getPunchlistDetails(paramsPunchlistId, {
        vendorSageId
      });
      setPunchlistDetails({
        ...res,
        punchListItems: sortPunchlistTodoItems(res.punchListItems, 'itemStatuses')
      });
      const filteredByVendor = filterByVendor(selectedVendorId, res.punchListItems);
      const filteredItems = filterByEmployee(selectedEmployeeId, filteredByVendor);

      setFilteredPunchlistDetails({
        ...punchlistDetail,
        punchListItems: filteredItems
      });

      if (resetFilters) {
        setSelectedVendorId('none');
        setHasAppliedFilters(false);
      }
    } catch (error) {
      enqueueSnackbar(`Error loading punchlist todos, please try again.`, {
        variant: 'error'
      });
      enqueueSnackbar(`Redirecting...`, {
        variant: 'info'
      });
      return setTimeout(function () {
        history.goBack();
      }, 1000);
    } finally {
      setLoadingTodos(false);
    }
  };

  useEffect(() => {
    if (punchlistItemId) {
      setCurrentTodoId(Number(punchlistItemId));
      showTodoModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [punchlistItemId]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchPunchlistDetails(true);
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getActionTitle = (item: IPunchlistTodoItem): string => {
    let action = 'Assigned To:';
    if (item.itemStatuses === 'Complete') {
      action = 'Completed By:';
    }
    return action;
  };

  const [isArchiving, setIsArchiving] = useState(false);

  const handleResetFilters = () => {
    setHasAppliedFilters(false);
    setSelectedVendorId('none');
    setSelectedEmployeeId('none');
  };

  return (
    <>
      <Page title={loadingTodos ? 'Loading...' : punchlistDetail?.name}>
        {loadingTodos && <Loader position="centered" type="inline" />}
        {(isDeleting || isArchiving || isDownloadingPdf) && (
          <Loader position="centered" type="fullscreen" />
        )}
        {!loadingTodos && (
          <>
            <PageTitleWithButton
              pageTitleProps={{
                title: punchlistDetail?.name,
                subTitle: punchlistDetail?.description
              }}
              button={
                <ButtonGroup fullWidth={isMobile}>
                  {(isAdmin || isFieldAdmin || isField || isVendor) && (
                    <Button
                      id="add-todo"
                      onClick={() => {
                        showPunchlistModal(true);
                      }}
                      variant="contained"
                      color="primary"
                      startIcon={<Edit />}
                      fullWidth={isMobile}
                    >
                      Edit
                    </Button>
                  )}

                  {!isMobile && (
                    <ReactToPrint
                      trigger={() => (
                        <Button
                          id="print-page"
                          variant="contained"
                          color="primary"
                          startIcon={<Print />}
                        >
                          Print
                        </Button>
                      )}
                      content={() => printRef.current}
                    />
                  )}
                  <Button
                    id="archive-todo"
                    onClick={async () => {
                      try {
                        setIsArchiving(true);
                        await updatePunchlist(punchlistDetail.punchListId, {
                          name: punchlistDetail.name,
                          description: punchlistDetail.description,
                          isArchived: !punchlistDetail.isArchived
                        });
                        await fetchPunchlistDetails();
                        setIsArchiving(false);
                      } catch {
                        setIsArchiving(false);
                      }
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<Archive />}
                    fullWidth={isMobile}
                  >
                    {punchlistDetail.isArchived
                      ? isMobile
                        ? 'Reactivate'
                        : 'Reactivate'
                      : isMobile
                      ? 'Archive'
                      : 'Archive'}
                  </Button>
                  <Button
                    id="download-pdf"
                    color="secondary"
                    variant="contained"
                    disabled={loadingTodos}
                    fullWidth={isMobile}
                    onClick={async () => {
                      try {
                        setIsDownloadingPdf(true);
                        const blob = await getPunchlistTodoListPdf({
                          includeArchived: false,
                          neighborhoodId: punchlistDetail?.neighborhoodId ?? '',
                          territoryId: punchlistDetail?.territoryId ?? '',
                          vendorSageId: selectedVendorId === 'none' ? undefined : selectedVendorId,
                          assignedTo: selectedEmployeeId === 'none' ? undefined : selectedEmployeeId,
                          sageJobId: punchlistDetail.sageJobId,
                        });

                        if (blob) {
                          downloadFile(blob, `${punchlistDetail.name}_todos.pdf`);
                        } else {
                          throw new Error('No PDF data received');
                        }
                      } catch (error) {
                        enqueueSnackbar(error?.message ?? `Error downloading PDF file`, {
                          variant: 'error'
                        });
                      } finally {
                        setIsDownloadingPdf(false);
                      }
                    }}
                    startIcon={<CloudDownload />}
                  >
                    PDF
                  </Button>
                </ButtonGroup>
              }
            />
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              mb={1}
              flexDirection={isMdMobile ? 'column' : 'row'}
              gap={isMdMobile ? 1 : 0}
            >
              <Box flex={1} width={isMdMobile ? '100%' : 'auto'}>
                <PunchlistDetailsFilters
                  vendors={vendors}
                  isLoading={loadingTodos}
                  selectedVendorId={selectedVendorId}
                  employees={employees}
                  setSelectedEmployeeId={setSelectedEmployeeId}
                  selectedEmployeeId={selectedEmployeeId}
                  setSelectedVendorId={setSelectedVendorId}
                  applyFilters={(clearFilters?: {
                    clearSelectedVendorId?: boolean;
                    clearSelectedEmployeeId?: boolean;
                  }) => {
                    if (clearFilters) {
                      if (clearFilters.clearSelectedVendorId) {
                        setSelectedVendorId('none');
                      }
                      if (clearFilters.clearSelectedEmployeeId) {
                        setSelectedEmployeeId('none');
                      }

                      filterPunchlist(
                        clearFilters.clearSelectedVendorId ? 'none' : selectedVendorId,
                        clearFilters.clearSelectedEmployeeId ? 'none' : selectedEmployeeId
                      );
                    } else {
                      filterPunchlist(selectedVendorId, selectedEmployeeId);
                    }
                  }}
                  hasAppliedFilters={hasAppliedFilters}
                  setHasAppliedFilters={setHasAppliedFilters}
                />
              </Box>
              <ButtonGroup sx={{ width: isMdMobile ? '100%' : 'auto' }}>
                <Button
                  id="add-todo"
                  onClick={() => {
                    showTodoModal(true);
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  fullWidth={isMdMobile}
                >
                  {isMobile ? 'Add' : 'Add Todo'}
                </Button>
                {(isAdmin || isField || isFieldAdmin) && (
                  <Button
                    id="add-template"
                    onClick={() => {
                      showIsTemplateModalOpen(true);
                    }}
                    variant="contained"
                    color="secondary"
                    startIcon={<Add />}
                    fullWidth={isMdMobile}
                  >
                    Add Template
                  </Button>
                )}
              </ButtonGroup>
            </Box>
            {filteredPunchlistDetails && (
              <PunchlistDetailsTodoList
                handleTitleClick={val => {
                  setCurrentTodoId(val.punchListItemId);
                  showTodoModal(true);
                }}
                route={route}
                punchlistDetail={filteredPunchlistDetails}
                setPunchlistDetails={(details: IPunchlistDetail) =>
                  setFilteredPunchlistDetails(details)
                }
                setIsDeleting={setIsDeleting}
                hasAppliedFilters={hasAppliedFilters}
                fetchPunchlistDetails={fetchPunchlistDetails}
              />
            )}
            {punchlistDetail?.punchListItems?.length > 0 && (
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button
                  id="add-todo"
                  onClick={() => {
                    showTodoModal(true);
                  }}
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                >
                  Add Todo
                </Button>
              </Box>
            )}
          </>
        )}
      </Page>
      {punchlistDetail && isTodoModalOpen && (
        <TodoDetailModal
          open
          punchlistItemId={currentTodoId}
          punchlistId={punchlistDetail.punchListId}
          handleClose={(shouldRefresh, shouldClose = true) => {
            // Clear any query params that might have been added from being linked by email
            history.replace(location.pathname);
            if (shouldClose) showTodoModal(false);
            setCurrentTodoId(null);
            if (shouldRefresh) {
              fetchPunchlistDetails();
            }
          }}
        />
      )}
      {punchlistDetail && (
        <PunchlistModal
          open={isPunchlistModalOpen}
          punchlist={punchlistDetail}
          handleClose={shouldRefresh => {
            showPunchlistModal(false);
            if (shouldRefresh) {
              fetchPunchlistDetails();
              handleResetFilters();
            }
          }}
        />
      )}
      <PunchlistTemplateModal
        isOpen={isTemplateModalOpen}
        isUpdating={isUpdating}
        handleClose={async templateId => {
          if (typeof templateId === 'number') {
            try {
              setUpdating(true);
              await addTemplateToPunchlist(punchlistDetail.punchListId, Number(templateId));
              await fetchPunchlistDetails();
              showIsTemplateModalOpen(false);
              enqueueSnackbar('Template items added!', {
                variant: 'success'
              });
              handleResetFilters();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error saving punchlist, please try again.', {
                variant: 'error'
              });
            } finally {
              setUpdating(false);
            }
          } else {
            showIsTemplateModalOpen(false);
          }
        }}
      />
      <div style={{ display: 'none' }}>
        <TableContainer component={Paper} ref={printRef}>
          <Typography variant="h4" style={{ margin: '20px' }}>
            {punchlistDetail?.name}
          </Typography>
          <Table aria-label="task table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: theme => theme.palette.primary.main }}>
                  Description
                </TableCell>
                <TableCell sx={{ color: theme => theme.palette.primary.main }}>
                  Assigned Vendor
                </TableCell>
                <TableCell sx={{ color: theme => theme.palette.primary.main }}>Action</TableCell>
                <TableCell sx={{ color: theme => theme.palette.primary.main }}>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPunchlistDetails?.punchListItems
                ?.filter(row => row.itemStatuses !== 'Complete')
                ?.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.vendorName}</TableCell>
                    <TableCell>
                      <span style={{ fontWeight: 'bold' }}>{getActionTitle(row)}</span>
                      {row.completedByDisplayName || row.assignedToDisplayName}
                    </TableCell>
                    <TableCell>{row.dueDate}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};
